import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { fetchParametresAdmin, revealParametreAdmin, updateParametreAdmin } from '../services/api';
import NoticeStripe from './NoticeStripe';

type Notice = { type: 'success' | 'error'; message: string } | null;

/** Les clés que ce bloc gouverne, et ce qu'on en dit à l'écran. */
const CHAMPS_STRIPE = [
  { cle: 'STRIPE_PUBLIC_KEY', libelle: 'Clé publique', aide: 'Commence par pk_test_ ou pk_live_' },
  { cle: 'STRIPE_SECRET_KEY', libelle: 'Clé secrète', aide: 'Commence par sk_test_ ou sk_live_' },
  { cle: 'STRIPE_WEBHOOK_SECRET', libelle: 'Secret du webhook', aide: 'Commence par whsec_' },
];

/**
 * Le prestataire d'encaissement, et ce qu'il sait faire.
 *
 * Stripe encaisse : la carte, Apple Pay, Google Pay, et le prélèvement SEPA
 * pour les règlements échelonnés. C'est le seul de la chaîne, et c'est voulu.
 *
 * Nous avons un temps ajouté un agrégateur bancaire pour lire le compte et
 * reconnaître les virements reçus. Il fonctionnait, mais demandait à chaque
 * association d'ouvrir un contrat chez un tiers et de le renouveler tous les
 * quatre-vingt-dix jours — beaucoup de démarches pour épargner la saisie de
 * quelques lignes par mois. Les virements se reportent désormais à la main
 * depuis l'écran Encaissements, où la référence retrouve la pièce toute
 * seule.
 *
 * Le compte Stripe appartient à l'association, qui renseigne ici ses propres
 * clés : l'argent arrive chez elle, ces accès lui appartiennent.
 */
export default function EncaissementEnLigne() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<Notice>(null);
  const [saisies, setSaisies] = useState<Record<string, string>>({});

  const parametres = useQuery({ queryKey: ['parametres-admin'], queryFn: fetchParametresAdmin });

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 4000);
  };

  const enregistrer = useMutation({
    mutationFn: ({ cle, valeur }: { cle: string; valeur: string }) => updateParametreAdmin(cle, valeur),
    onSuccess: (_d, { cle }) => {
      annoncer('success', 'Enregistré.');
      setSaisies((prev) => {
        const suite = { ...prev };
        delete suite[cle];
        return suite;
      });
      queryClient.invalidateQueries({ queryKey: ['parametres-admin'] });
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  if (parametres.isPending) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const tous: any[] = Object.values(parametres.data?.groupes ?? {}).flat() as any[];
  const trouver = (cle: string) => tous.find((p) => p.cle === cle);
  // « Configuré » se lit sur le serveur, pas sur la valeur.
  //
  // L'API ne renvoie jamais une valeur sensible : elle vaut null même quand la
  // clé est parfaitement posée. Cet écran la testait pourtant, et annonçait
  // donc « Non configuré : le paiement par carte est fermé » à des
  // associations qui encaissaient depuis des mois.
  const stripePret = (parametres.data as any)?.stripe?.configure === true;
  // Le mode vient du serveur. L'écran lisait le préfixe « sk_test_ » dans
  // l'aperçu de la clé secrète ; l'aperçu ne montre plus que des puces, et
  // c'est de toute façon le serveur qui fait autorité sur ce que Stripe fait.
  const modeTest = (parametres.data as any)?.stripe?.modeTest === true;

  return (
    <div className="space-y-5">
      {notice && (
        <div
          className={`flex items-start gap-2 rounded-lg px-4 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span>{notice.message}</span>
        </div>
      )}

      {/* ------------------------------- Stripe ------------------------------- */}
      <Prestataire
        icone={CreditCard}
        titre="Stripe — carte, Apple Pay, Google Pay"
        actif={stripePret}
        resume={
          stripePret
            ? modeTest
              ? 'Configuré en mode test : aucun paiement réel.'
              : 'Configuré en mode réel.'
            : 'Non configuré : le paiement par carte est fermé.'
        }
        lien="https://dashboard.stripe.com/apikeys"
      >
        <p className="text-sm text-gray-600 mb-3">
          C'est ce qui encaisse : cotisations réglées en ligne, billetterie, boutique, dons. Le compte
          Stripe est celui de votre association — ouvert à son nom, alimentant son IBAN. Nous n'y
          avons aucun accès, et vous en gardez la maîtrise entière.
        </p>

        {CHAMPS_STRIPE.map((champ) => (
          <ChampSecret
            key={champ.cle}
            champ={champ}
            parametre={trouver(champ.cle)}
            valeur={saisies[champ.cle]}
            onSaisie={(v) => setSaisies((prev) => ({ ...prev, [champ.cle]: v }))}
            onEnregistrer={() => enregistrer.mutate({ cle: champ.cle, valeur: saisies[champ.cle] ?? '' })}
            enCours={enregistrer.isPending}
          />
        ))}

        {stripePret && (
          <p
            className={`mt-3 rounded-lg px-3 py-2 text-sm ${
              modeTest
                ? 'bg-amber-50 border border-amber-200 text-amber-800'
                : 'bg-green-50 border border-green-200 text-green-800'
            }`}
          >
            {modeTest
              ? "Mode test : les paiements ne débitent personne. Le mode se lit sur le préfixe de la clé secrète, pas sur une case — une case ne changerait rien à ce que fait Stripe."
              : 'Mode réel : les paiements débitent vraiment.'}
          </p>
        )}

        {/* La marche à suivre là où l'on demande les clés : une notice rangée
            ailleurs se lit après coup, quand la configuration est déjà à
            moitié faite et à moitié fausse. */}
        <NoticeStripe />
      </Prestataire>

    </div>
  );
}

/** Un prestataire : son état d'un coup d'œil, ses réglages en dessous. */
function Prestataire({
  icone: Icone,
  titre,
  actif,
  resume,
  lien,
  children,
}: {
  icone: any;
  titre: string;
  actif: boolean;
  resume: string;
  lien: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <span
            className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
              actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
          >
            <Icone className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">{titre}</h4>
            <p className="text-xs text-gray-500">{resume}</p>
          </div>
        </div>
        <a
          href={lien}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
        >
          Ouvrir un compte
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      {children}
    </section>
  );
}

/**
 * Une clé d'encaissement : masquée, remplaçable, et consultable au besoin.
 *
 * Elle se remplace le plus souvent sans qu'on la relise — mais il faut
 * pouvoir la relire : pour vérifier qu'on a bien collé la clé réelle et non
 * celle de test, ou pour la reporter dans le tableau de bord Stripe. D'où le
 * 👀, et le mot de passe qu'il réclame.
 */
function ChampSecret({
  champ,
  parametre,
  valeur,
  onSaisie,
  onEnregistrer,
  enCours,
}: {
  champ: { cle: string; libelle: string; aide: string };
  parametre: any;
  valeur?: string;
  onSaisie: (v: string) => void;
  onEnregistrer: () => void;
  enCours: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [enClair, setEnClair] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);

  // Une valeur sensible n'est jamais renvoyée en clair par l'API : « posé » se
  // lit sur estRenseigne, pas sur la valeur. Se fier à celle-ci faisait dire
  // « non configuré » à un champ parfaitement rempli, et proposait
  // « Enregistrer » là où il fallait lire « Remplacer ».
  const pose = parametre?.estRenseigne === true;

  /**
   * Comme dans les Paramètres : on tente d'abord sans mot de passe. Le super
   * administrateur passe toujours, et les autres passent tant que la page est
   * déverrouillée — le serveur garde l'ouverture un quart d'heure. Le champ
   * de mot de passe n'apparaît qu'au premier refus.
   */
  const reveler = async (mdp = '') => {
    setErreur(null);
    setChargement(true);
    try {
      const d = await revealParametreAdmin(champ.cle, mdp);
      setEnClair(d.valeur ?? '');
      setMotDePasse('');
      setOuvert(false);
    } catch (e: any) {
      if (mdp === '') {
        setOuvert(true);
      } else {
        setErreur(e?.message ?? 'Impossible de révéler la valeur.');
      }
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="mb-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[12rem] text-xs text-gray-500">
          {champ.libelle}
          <input
            type="password"
            autoComplete="off"
            value={valeur ?? ''}
            onChange={(e) => onSaisie(e.target.value)}
            placeholder={pose ? '•••••••••••• (en place)' : champ.aide}
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800"
          />
        </label>

        {pose && (
          <button
            type="button"
            onClick={() => {
              if (enClair !== null || ouvert) {
                // Refermer : la valeur disparaît, le champ de mot de passe aussi.
                setOuvert(false);
                setEnClair(null);
                setErreur(null);
                setMotDePasse('');

                return;
              }
              void reveler();
            }}
            disabled={chargement}
            title={enClair !== null || ouvert ? 'Masquer' : 'Afficher la valeur enregistrée'}
            aria-label={enClair !== null || ouvert ? 'Masquer' : 'Afficher la valeur enregistrée'}
            className="px-2 py-2 text-base leading-none hover:scale-110 transition-transform disabled:opacity-50"
          >
            <span aria-hidden="true">{enClair !== null || ouvert ? '🙈' : '👀'}</span>
          </button>
        )}

        <button
          onClick={onEnregistrer}
          disabled={enCours || (valeur ?? '') === ''}
          className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40"
        >
          {pose ? 'Remplacer' : 'Enregistrer'}
        </button>
      </div>

      {ouvert && (
        <div className="mt-2 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <input
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void reveler(motDePasse); }}
            placeholder="Votre mot de passe"
            className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            autoFocus
          />
          <button
            type="button"
            onClick={() => void reveler(motDePasse)}
            disabled={chargement || motDePasse.trim() === ''}
            className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {chargement ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span aria-hidden="true">👀</span>}
            Révéler
          </button>
        </div>
      )}

      {erreur && <p className="text-xs text-red-600 mt-1">{erreur}</p>}
      {enClair !== null && (
        <code className="block mt-1 p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs font-mono break-all">
          {enClair}
        </code>
      )}
    </div>
  );
}
